import React, { useState, useEffect } from "react";
import { api } from "../api/api";

const VulnerabilityForm = ({
  selectedAssessment,
  selectedVersion,
  selectedVulnerability,
  onClose,
  onSave,
  userRoles,
  assessmentStatus,
  currentUser,
  // ✅ NEW: Accept the isReadOnly prop from the parent component.
  isReadOnly = false,
}) => {
  // ✅ MOVED: All hooks are now at the top level of the component.
  const [vulnerability, setVulnerability] = useState({
    vulnId: null,
    vulnTitle: "",
    severityId: "",
    urls: "",
    category: "",
    attributingFactor: "",
    description: "",
    businessImpact: "",
    recommendations: "",
    testId: "",
    cwe: "",
    cve: "",
    patchingStatus: "",
    commentsByClient: "",
    commentsByVarutraTeam: "",
    assessmentVersionId: selectedVersion?.assessmentVersionId || "",
    screenshots: [],
  });
  const [screenshotFiles, setScreenshotFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Your useEffect hook is also moved to the top
  useEffect(() => {
    // We check for selectedVersion here to avoid errors when it's null initially
    if (selectedVulnerability) {
      setVulnerability({
        ...selectedVulnerability,
        urls: selectedVulnerability.urls || "",
        attributingFactor: selectedVulnerability.attributingFactor || "",
        testId:
          selectedVulnerability.testId ||
          selectedVulnerability.typeoftesting ||
          "",
        screenshots: Array.isArray(selectedVulnerability.screenshots)
          ? selectedVulnerability.screenshots
          : [],
        assessmentVersionId:
          selectedVulnerability.assessmentVersionId ||
          selectedVersion?.assessmentVersionId ||
          "",
      });
    } else {
      setVulnerability({
        vulnId: null,
        vulnTitle: "",
        severityId: "",
        urls: "",
        category: "",
        attributingFactor: "",
        description: "",
        businessImpact: "",
        recommendations: "",
        testId: "",
        cwe: "",
        cve: "",
        patchingStatus: "",
        commentsByClient: "",
        commentsByVarutraTeam: "",
        assessmentVersionId: selectedVersion?.assessmentVersionId || "",
        screenshots: [],
      });
    }
    setScreenshotFiles([]);
  }, [selectedVulnerability, selectedVersion]);

  // ✅ Your original guard clause now comes AFTER all hooks have been declared.
  if (!selectedAssessment || !selectedVersion) {
    return (
      <div className="vulnerability-form-error-container">
        <p>
          Please select an assessment and version to add or edit a
          vulnerability.
        </p>
        <button onClick={onClose} className="close-button">
          Close
        </button>
      </div>
    );
  }

  // --- Role-based access control checks ---
  const isManagerialRole =
    Array.isArray(userRoles) &&
    (userRoles.includes("Manager") || userRoles.includes("AssisstantManager"));
  const isTester = Array.isArray(userRoles) && userRoles.includes("Tester");
  const isClient = Array.isArray(userRoles) && userRoles.includes("Client");

  const isTesterAssignedToVersion =
    isTester &&
    currentUser?.name &&
    Array.isArray(selectedVersion?.preparedBy) &&
    selectedVersion.preparedBy.includes(currentUser.name);

  // Base permissions are defined here
  const canTesterEditBase =
    isTesterAssignedToVersion && assessmentStatus === "In Progress";
  const canClientEditBase = isClient && assessmentStatus === "L2 Approved";

  // ✅ UPDATED: Final permission flags now respect the isReadOnly prop from the parent.
  // If isReadOnly is true, all edit permissions are revoked.
  const canEditGeneralFields =
    !isReadOnly && (isManagerialRole || canTesterEditBase);
  const canEditClientComments =
    !isReadOnly && (isManagerialRole || canClientEditBase);
  const canEditPatchingStatus =
    !isReadOnly && (isManagerialRole || canTesterEditBase || canClientEditBase);
  const canSubmit = !isReadOnly && (isManagerialRole || canTesterEditBase);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVulnerability((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setScreenshotFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setError(
        "You do not have permission to save changes in the current assessment state."
      );
      return;
    }

    if (!vulnerability.testId) {
      setError("Testing Type is required to save a vulnerability.");
      return;
    }

    const assessmentId = selectedAssessment.assessmentId;
    const assessmentVersionId = selectedVersion.assessmentVersionId;

    setLoading(true);
    setError("");

    const formData = new FormData();
    Object.entries(vulnerability).forEach(([key, value]) => {
      if (value !== null && value !== undefined && key !== "screenshots") {
        formData.append(key, value);
      }
    });

    screenshotFiles.forEach((file) => {
      formData.append("screenshotFiles", file);
    });

    try {
      const isUpdate = !!vulnerability.vulnId;

      if (isUpdate) {
        const endpoint = `/vulnerabilities/${vulnerability.vulnId}`;
        await api.put(endpoint, formData);
      } else {
        const endpoint = `/assessments/${assessmentId}/versions/${assessmentVersionId}/vulnerabilities`;
        await api.post(endpoint, formData);
      }

      setSuccess(true);
      setScreenshotFiles([]);
      alert("Vulnerability saved successfully!");

      setTimeout(() => {
        onSave();
      }, 1000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An unknown error occurred.";
      setError(`Failed to save vulnerability: ${errorMessage}`);
      alert(`Failed to save vulnerability: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .vulnerability-form-error-container {
            padding: 2rem;
            background-color: #ffebee;
            border: 1px solid #ef9a9a;
            border-radius: 8px;
            text-align: center;
            color: #c62828;
            max-width: 600px;
            margin: 2rem auto;
          }
          .vulnerability-form {
            background-color: #ffffff;
            padding: 2rem;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            max-width: 800px;
            margin: 2rem auto;
            font-family: 'Inter', sans-serif;
          }
          .form-title {
            text-align: center;
            font-size: 1.75rem;
            font-weight: 700;
            color: #1a202c;
            margin-bottom: 1.5rem;
          }
          .error-message, .success-message {
            padding: 0.75rem;
            border-radius: 8px;
            margin-bottom: 1rem;
            text-align: center;
          }
          .error-message { color: #e53e3e; background-color: #fed7d7; }
          .success-message { color: #38a169; background-color: #c6f6d5; }
          .form-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1rem;
          }
          .form-input, .form-select, .form-textarea {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            font-size: 1rem;
            color: #4a5568;
            transition: border-color 0.2s;
          }
          .form-input:focus, .form-select:focus, .form-textarea:focus {
            outline: none;
            border-color: #4299e1;
            box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
          }
          .form-input:disabled, .form-select:disabled, .form-textarea:disabled {
            background-color: #edf2f7;
            cursor: not-allowed;
            opacity: 0.7;
          }
          .form-textarea {
            grid-column: 1 / -1;
            resize: vertical;
          }
          .form-file-input {
            padding: 0.75rem;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            background-color: #f7fafc;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          .form-file-input:hover { background-color: #edf2f7; }
          .form-file-input:disabled {
            background-color: #edf2f7;
            cursor: not-allowed;
            opacity: 0.7;
          }
          .screenshots-display-section { margin-top: 2rem; }
          .form-section-title {
            font-size: 1.25rem;
            font-weight: 600;
            color: #2d3748;
            margin-bottom: 1rem;
          }
          .cancel-button, .save-button {
            padding: 0.75rem 1.5rem;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            border: none;
          }
          .cancel-button { background-color: #cbd5e0; color: #4a5568; }
          .cancel-button:hover { background-color: #a0aec0; }
          .save-button { background-color: #4299e1; color: #ffffff; }
          .save-button:hover { background-color: #3182ce; }
          .cancel-button:disabled, .save-button:disabled {
            cursor: not-allowed;
            opacity: 0.6;
          }
          .screenshots-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
            gap: 0.75rem;
          }
          .screenshot-thumbnail-container {
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            cursor: pointer;
            aspect-ratio: 4 / 3; 
          }
          .screenshot-thumbnail-image {
            width: 100%;
            height: 100%;
            display: block;
            object-fit: cover;
            transition: transform 0.2s ease-in-out;
          }
          .screenshot-thumbnail-container:hover .screenshot-thumbnail-image {
            transform: scale(1.05);
          }
          .image-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            padding: 20px;
          }
          .image-modal-content {
            max-width: 90vw;
            max-height: 90vh;
            border-radius: 8px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
            animation: zoomIn 0.3s ease-in-out;
          }
          @keyframes zoomIn {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
      <form onSubmit={handleSubmit} className="vulnerability-form">
        <h2 className="form-title">
          {isReadOnly
            ? "View Vulnerability"
            : vulnerability.vulnId
            ? "Edit Vulnerability"
            : "Add New Vulnerability"}
        </h2>

        {error && <p className="error-message">{error}</p>}
        {success && <p className="success-message">Saved successfully!</p>}

        <div className="form-grid">
          <label className="form-label">
            Vulnerability Title
            <input
              type="text"
              name="vulnTitle"
              placeholder="Vulnerability Title"
              value={vulnerability.vulnTitle}
              onChange={handleChange}
              required
              className="form-input"
              disabled={!canEditGeneralFields}
            />
          </label>
          <label className="form-label">
            Severity
            <select
              name="severityId"
              value={vulnerability.severityId}
              onChange={handleChange}
              required
              className="form-select"
              disabled={!canEditGeneralFields}
            >
              <option value="">Select Severity</option>
              <option value="1">Critical</option>
              <option value="2">High</option>
              <option value="3">Medium</option>
              <option value="4">Low</option>
              <option value="5">Informative</option>
            </select>
          </label>
          <label className="form-label">
            Affected URLs
            <input
              type="text"
              name="urls"
              placeholder="Affected URLs"
              value={vulnerability.urls}
              onChange={handleChange}
              className="form-input"
              disabled={!canEditGeneralFields}
            />
          </label>
          <label className="form-label">
            Category
            <input
              type="text"
              name="category"
              placeholder="Category"
              value={vulnerability.category}
              onChange={handleChange}
              className="form-input"
              disabled={!canEditGeneralFields}
            />
          </label>
          <label className="form-label">
            Attributing Factor
            <select
              name="attributingFactor"
              value={vulnerability.attributingFactor}
              onChange={handleChange}
              required
              className="form-select"
              disabled={!canEditGeneralFields}
            >
              <option value="">Select Attributing Factor</option>
              <option value="Coding Error">Coding Error</option>
              <option value="Configuration Error">Configuration Error</option>
              <option value="Vulnerabale Software Versions">
                Vulnerabale Software Versions
              </option>
              <option value="Design Error">Design Error</option>
              <option value="Deployment Error">Deployment Error</option>
              <option value="Operation Error">Operation Error</option>
            </select>
          </label>
          <label className="form-label">
            Testing Type
            <select
              name="testId"
              value={vulnerability.testId}
              onChange={handleChange}
              required
              className="form-select"
              disabled={!canEditGeneralFields}
            >
              <option value="">Select Testing Type</option>
              <option value="1">Gray Box PT</option>
              <option value="2">Black Box PT</option>
            </select>
          </label>
          <label className="form-label">
            CWE
            <input
              type="text"
              name="cwe"
              placeholder="CWE"
              value={vulnerability.cwe}
              onChange={handleChange}
              className="form-input"
              disabled={!canEditGeneralFields}
            />
          </label>
          <label className="form-label">
            CVE
            <input
              type="text"
              name="cve"
              placeholder="CVE"
              value={vulnerability.cve}
              onChange={handleChange}
              className="form-input"
              disabled={!canEditGeneralFields}
            />
          </label>
          <label className="form-label">
            Patching Status
            <select
              name="patchingStatus"
              value={vulnerability.patchingStatus}
              onChange={handleChange}
              className="form-select"
              disabled={!canEditPatchingStatus}
            >
              <option value="">Select patching Status</option>
              <option value="Patched">Patched</option>
              <option value="Not Patched">Not Patched</option>
              <option value="Having Query">Having Query</option>
              <option value="Business Exception">Business Exception</option>
              <option value="Not Assessed">Not Assessed</option>
              <option value="Not Applicable">Not Applicable</option>
            </select>
          </label>
          <label className="form-label full-width">
            Description
            <textarea
              name="description"
              placeholder="Description"
              value={vulnerability.description}
              onChange={handleChange}
              rows={3}
              className="form-textarea"
              disabled={!canEditGeneralFields}
            ></textarea>
          </label>
          <label className="form-label full-width">
            Business Impact
            <textarea
              name="businessImpact"
              placeholder="Business Impact"
              value={vulnerability.businessImpact}
              onChange={handleChange}
              rows={3}
              className="form-textarea"
              disabled={!canEditGeneralFields}
            ></textarea>
          </label>
          <label className="form-label full-width">
            Recommendations
            <textarea
              name="recommendations"
              placeholder="Recommendations"
              value={vulnerability.recommendations}
              onChange={handleChange}
              rows={3}
              className="form-textarea"
              disabled={!canEditGeneralFields}
            ></textarea>
          </label>
          <label className="form-label full-width">
            Comments by Testing team
            <textarea
              name="commentsByVarutraTeam"
              placeholder="Comments by Varutra Team"
              value={vulnerability.commentsByVarutraTeam}
              onChange={handleChange}
              rows={2}
              className="form-textarea"
              disabled={!canEditGeneralFields}
            ></textarea>
          </label>
          <label className="form-label full-width">
            Comments by Client
            <textarea
              name="commentsByClient"
              placeholder="Comments by Client"
              value={vulnerability.commentsByClient}
              onChange={handleChange}
              rows={2}
              className="form-textarea"
              disabled={!canEditClientComments}
            ></textarea>
          </label>
          <label className="form-label full-width">
            Screenshots
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="form-file-input"
              disabled={!canEditGeneralFields}
            />
          </label>
        </div>

        {selectedVulnerability && vulnerability.screenshots.length > 0 && (
          <div className="screenshots-display-section">
            <h3 className="form-section-title">Existing Screenshots</h3>
            <div className="screenshots-grid">
              {vulnerability.screenshots.map((screenshot, index) => (
                <div
                  key={screenshot.sr_no || index}
                  className="screenshot-thumbnail-container"
                  onClick={() =>
                    setEnlargedImage(
                      `https://api1-vapt-report.infosharesystems.io${screenshot.imageUrl}`
                    )
                  }
                >
                  <img
                    src={`https://api1-vapt-report.infosharesystems.io${screenshot.imageUrl}`}
                    alt={`Screenshot ${index + 1}`}
                    className="screenshot-thumbnail-image"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {screenshotFiles.length > 0 && (
          <div className="screenshots-display-section">
            <h3 className="form-section-title">New Screenshots to Upload</h3>
            <ul>
              {screenshotFiles.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end space-x-4 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="cancel-button"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="save-button"
            disabled={loading || !canSubmit}
          >
            {loading ? "Saving..." : vulnerability.vulnId ? "Update" : "Save"}
          </button>
        </div>
      </form>

      {enlargedImage && (
        <div
          className="image-modal-overlay"
          onClick={() => setEnlargedImage(null)}
        >
          <img
            src={enlargedImage}
            alt="Enlarged screenshot"
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default VulnerabilityForm;
